import React, { useState, useEffect, useCallback } from 'react';
import { Sun, Plus, Check, Loader2 } from 'lucide-react';
import { EmailSignalEngine } from '../../utils/emailSignalData';
import { addToPriorityQueue } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui';

function tierColor(tier) {
  switch (tier) {
    case 'critical': return '#EF4444';
    case 'high': return '#F59E0B';
    case 'moderate': return '#2C5FA8';
    default: return '#10B981';
  }
}

export default function HuddleBrief() {
  const { getToken } = useAuth();
  const [brief, setBrief] = useState(null);
  const [addedIds, setAddedIds] = useState(() => EmailSignalEngine.getPriorityQueueThreadIds());
  const [loadingId, setLoadingId] = useState(null);
  const [justAddedId, setJustAddedId] = useState(null); // show "Added to Queue" briefly after click

  useEffect(() => {
    setBrief(EmailSignalEngine.getHuddleToday());
  }, []);

  const handleAddToPriorityQueue = useCallback(async (t) => {
    if (addedIds.includes(t.id)) return;
    setLoadingId(t.id);
    try {
      await addToPriorityQueue('all', {
        title: t.subjectMasked,
        description: t.explanation,
        category: t.category,
        priority: t.riskTier === 'critical' ? 'high' : t.riskTier === 'high' ? 'high' : 'medium',
        estimatedTime: 15,
        impact: `Risk ${t.riskScore} · ${t.uniqueActors} actors`,
        sourceData: { threadId: t.id },
        type: 'emailSignalThread',
        dueDate: new Date().toLocaleDateString('en-CA'),
      }, getToken);
      EmailSignalEngine.addThreadToPriorityQueueTracking(t.id);
      setAddedIds(prev => [...prev, t.id]);
      setJustAddedId(t.id);
      setTimeout(() => setJustAddedId(null), 2500);
    } catch (err) {
      console.error('Failed to add to Priority Queue:', err);
    } finally {
      setLoadingId(null);
    }
  }, [addedIds, getToken]);

  if (!brief) return <div className="animate-pulse h-64 bg-gray-100 rounded-lg" />;

  return (
    <div className="space-y-6">
      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-royal" style={{ borderLeft: '4px solid #2C5FA8' }}>
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sun size={20} color="#2C5FA8" />
            Morning Huddle Brief
          </div>
          <div className="text-sm text-gray-500">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
        </div>
        <div className="p-5 space-y-6">
          {/* Recommended focus */}
          <div style={{ padding: 16, background: '#EFF6FF', borderRadius: 8, borderLeft: '4px solid #2C5FA8' }}>
            <div className="text-body-sm font-semibold" style={{ color: '#1E40AF', marginBottom: 4 }}>Recommended focus today</div>
            <div className="text-base" style={{ color: '#1E3A8A' }}>{brief.recommendedFocus}</div>
          </div>

          {/* Top 5 at-risk */}
          <div>
            <h3 className="text-base font-semibold" style={{ marginBottom: 12 }}>Top 5 at-risk threads</h3>
            <div className="space-y-3">
              {brief.topAtRisk.map((t, i) => {
                const inQueue = addedIds.includes(t.id);
                const loading = loadingId === t.id;
                return (
                  <div
                    key={t.id}
                    style={{
                      padding: 12,
                      background: '#F9FAFB',
                      borderRadius: 8,
                      borderLeft: `4px solid ${tierColor(t.riskTier)}`,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{t.subjectMasked}</div>
                        <div className="text-body text-text-muted">{t.explanation}</div>
                        <div className="text-body-sm text-text-muted" style={{ marginTop: 6 }}>
                          {t.category} · {t.uniqueActors} actors · {t.replyCycles} cycles
                        </div>
                    </div>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 700,
                        background: `${tierColor(t.riskTier)}20`,
                        color: tierColor(t.riskTier),
                        flexShrink: 0,
                      }}
                    >
                      {t.riskScore}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={inQueue || loading}
                      onClick={() => handleAddToPriorityQueue(t)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        color: inQueue ? '#38A169' : '#2C5FA8',
                        cursor: inQueue || loading ? 'default' : 'pointer',
                        padding: '6px 10px',
                        borderRadius: 6,
                      }}
                      onMouseEnter={(e) => {
                        if (!inQueue && !loading) {
                          e.currentTarget.style.color = '#00CED1';
                          e.currentTarget.style.background = '#F7F9FC';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!inQueue) {
                          e.currentTarget.style.color = '#2C5FA8';
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      {loading ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : inQueue ? (
                        <>
                          <Check size={14} />
                          {justAddedId === t.id ? '✓ Added to Queue' : '✓ In Queue'}
                        </>
                      ) : (
                        <>
                          <Plus size={14} />
                          Add to Priority Queue
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              );
              })}
            </div>
          </div>

          {/* 7-day latency trend */}
          <div>
            <h3 className="text-base font-semibold" style={{ marginBottom: 8 }}>7-day latency trend</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="text-2xl font-bold text-text-dark">{brief.latencyTrend.value} {brief.latencyTrend.unit}</span>
              <span
                className={`text-body-sm font-semibold ${brief.latencyTrend.direction === 'improving' ? 'text-green-nn' : brief.latencyTrend.direction === 'degrading' ? 'text-red-critical' : 'text-text-dark'}`}
              style={{
                  padding: '4px 10px',
                  borderRadius: 6,
                  background: brief.latencyTrend.direction === 'improving' ? '#D1FAE5' : brief.latencyTrend.direction === 'degrading' ? '#FEE2E2' : '#E5E7EB',
                }}
              >
                {brief.latencyTrend.direction}
              </span>
            </div>
          </div>

          {/* Category congestion */}
          <div>
            <h3 className="text-base font-semibold" style={{ marginBottom: 8 }}>Category congestion</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
              {brief.categoryCongestion.map(c => (
                <div key={c.name} style={{ padding: 12, background: '#F9FAFB', borderRadius: 8 }}>
                  <div className="text-body font-semibold">{c.name}</div>
                  <div className="text-body-sm text-text-muted">Volume: {c.volume}</div>
                  <div className="text-body-sm text-text-muted">Escalations: {c.escalationCount}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Actor workload snapshot */}
          {brief.actorWorkload && brief.actorWorkload.length > 0 && (
            <div>
              <h3 className="text-base font-semibold" style={{ marginBottom: 8 }}>Actor workload (deviation)</h3>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {brief.actorWorkload.map(a => (
                  <div key={a.actorHash} className="text-body" style={{ padding: 12, background: '#FEF3C7', borderRadius: 8 }}>
                    <strong>{a.displayName}</strong>: {a.meanHours}h mean (deviation)
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
